'use client';
import { useState } from 'react';
import { GETv1HistoryBySymbol } from '@/api/quotes';
import ResolutionTabs from '@/components/atoms/Tabs/ResolutionTabs';
import { QuotesCodeMapping } from '@/constants/quote/quote';
import { useQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { RectangleSkeleton } from '@/components/atoms/Skeleton';
import { Period, getPeriodAgoTimestampFromNow } from '@/utils/time';
import { type History } from '@/types/history';
import cx from 'classnames';

const PerformanceCompare = dynamic(() => import('@/components/third-party/LightWeightChart/PerformaceCompare'), {
  loading: () => <LoadingSkeleton height={550} />,
  ssr: false
});

type PerformanceCompareClientProps = {
  symbols: string[];
  isMobile?: boolean;
};

const LoadingSkeleton = ({ height }: { height?: number }) => <RectangleSkeleton height={height} width='100%' />;

const TABS = [
  { label: '年至今', value: Period.YEAR_TO_DATE },
  { label: '1週', value: Period.WEEK },
  { label: '1月', value: Period.MONTH },
  { label: '3月', value: Period.THREE_MONTH },
  { label: '6月', value: Period.SIX_MONTH },
  { label: '1年', value: Period.YEAR },
  { label: '3年', value: Period.THREE_YEAR },
  { label: '5年', value: Period.FIVE_YEAR }
];

type HistoriesProps = {
  data: History;
  statusCode: number;
  message: string;
};

const select = (histories: HistoriesProps[]) => {
  const data: Record<string, { value: number; time: number }[]> = {};
  // 將線圖資料整理成 {key1: {time, value}[], key2: {time, value}[], ...] 格式
  histories.forEach(res => {
    const history = res?.data || {};
    const close = history?.c;
    const quote = history?.quote;
    const name = quote[QuotesCodeMapping.CHINESE_NAME];
    const firstClose = close[close.length - 1];
    const format = history.t?.map((time: number, index: number) => ({
      time,
      value: close[index] / firstClose - 1
    }));
    format.sort((a: { time: number }, b: { time: number }) => a.time - b.time);

    data[name] = format;
  });

  return data;
};

const transResolutionToFromTo = (period: Period) => {
  const to = getPeriodAgoTimestampFromNow(period);
  const from = Math.floor(Date.now() / 1000);

  return { from, to };
};

const queryFn = async ({ queryKey }: { queryKey: string[] }): Promise<HistoriesProps[]> => {
  const symbols = JSON.parse(queryKey[1]);
  const resolution = queryKey[2];

  const { from, to } = transResolutionToFromTo(resolution as Period);

  try {
    return await Promise.all(
      symbols.map((symbol: string) => GETv1HistoryBySymbol({ symbol, resolution: 'D', from, to, quote: 1 }))
    );
  } catch (e) {
    console.error('[績效比較API錯誤]:', e);
    return [];
  }
};

const PerformanceCompareClient = ({ symbols, isMobile }: PerformanceCompareClientProps) => {
  const [resolution, setResolution] = useState<Period>(TABS[0].value);
  const limit = isMobile ? 4 : undefined;
  const height = isMobile ? 400 : 550;

  const { data } = useQuery({
    queryKey: ['performance-compare-chart', JSON.stringify(symbols), resolution.toString()],
    queryFn,
    select,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    placeholderData: prev => prev
  });

  const onClick = (index: number) => {
    setResolution(TABS?.[index]?.value);
  };

  return (
    <>
      <div className={cx('mb-3', { 'mr-4': isMobile })}>
        <ResolutionTabs tabs={TABS} onClick={onClick} limit={limit} />
      </div>
      {data ? (
        <PerformanceCompare data={data} height={height} isMobile={isMobile} />
      ) : (
        <LoadingSkeleton height={height} />
      )}
    </>
  );
};

export default PerformanceCompareClient;
